/**
 * Mint-and-copy dialog for a voice clip share link.
 *
 * The expiry is chosen every time rather than remembered, and the shortest option is first. A
 * remembered default drifts towards whatever was convenient once and then quietly applies to every
 * clip after — which is the wrong direction for a link that lets anyone holding it hear a recording
 * of a player.
 *
 * The note is optional but offered, because a bare link with no context invites the recipient to ask
 * what they are listening for, and the answer usually ends up somewhere far less durable than the
 * page itself.
 */

import { useEffect, useState } from "react";
import { createShareLink, revokeShareLink, type ShareLink } from "../api";
import { IconClose } from "./Icons";

interface Props {
  eventId: number;
  player: string;
  csrfToken: string;
  onClose: () => void;
}

const TTL_OPTIONS = [
  { label: "1 hour", seconds: 3_600 },
  { label: "24 hours", seconds: 86_400 },
  { label: "7 days", seconds: 604_800 },
];

export function ShareDialog({ eventId, player, csrfToken, onClose }: Props): JSX.Element {
  const [ttl, setTtl] = useState(86_400);
  const [note, setNote] = useState("");
  const [link, setLink] = useState<ShareLink | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const create = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setLink(await createShareLink(eventId, ttl, note, csrfToken));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not create a link.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (): Promise<void> => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is refused on insecure origins and in some browsers. The input below is
      // selectable, so there is always a manual route — no need to make this an error.
    }
  };

  const revoke = async (): Promise<void> => {
    if (!link) return;
    await revokeShareLink(link.token, csrfToken);
    setLink(null);
  };

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Share the voice clip for ${player}`}
      >
        <div className="modal-head">
          <h2>Share clip &middot; {player}</h2>
          <span className="grow" />
          <button type="button" className="btn" onClick={onClose} aria-label="Close">
            <IconClose />
          </button>
        </div>

        {!link ? (
          <>
            <div className="setting" style={{ paddingTop: 0 }}>
              <div className="setting-info">
                <b>Link expires after</b>
                <p>The link stops working on its own. You can also revoke it early.</p>
              </div>
              <div className="setting-control">
                <div className="seg">
                  {TTL_OPTIONS.map((option) => (
                    <button
                      key={option.seconds}
                      type="button"
                      aria-pressed={ttl === option.seconds}
                      onClick={() => setTtl(option.seconds)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="setting">
              <div className="setting-info">
                <b>Note (optional)</b>
                <p>Shown on the page, so the recipient knows what they are listening for.</p>
                <input
                  className="input"
                  style={{ width: "100%", marginTop: 8 }}
                  maxLength={200}
                  placeholder="e.g. mute appeal for ticket #412"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
              </div>
            </div>

            {error && (
              <div className="note" data-k="danger">
                {error}
              </div>
            )}

            <div className="row-end">
              <button type="button" className="btn" onClick={onClose}>
                Cancel
              </button>
              <button type="button" className="btn" data-variant="primary" onClick={create} disabled={busy}>
                {busy ? "Creating…" : "Create link"}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="sub">
              Anyone with this link can play the clip until it expires
              {" "}
              {new Date(link.expiresAt).toLocaleString()}.
            </p>

            <div className="row" style={{ marginTop: 4 }}>
              <input
                className="input"
                style={{ flex: 1, fontFamily: "var(--mono)", fontSize: 12.5 }}
                readOnly
                value={link.url}
                onFocus={(event) => event.currentTarget.select()}
              />
              <button type="button" className="btn" data-variant="primary" onClick={copy}>
                {copied ? "Copied" : "Copy"}
              </button>
            </div>

            <div className="note" style={{ marginTop: 14 }}>
              This is a recording of a player. It is not password protected — anyone the link reaches
              can hear it. Every play is recorded in the audit log against your name.
            </div>

            <div className="row-end">
              <button type="button" className="btn" onClick={revoke}>
                Revoke
              </button>
              <button type="button" className="btn" data-variant="primary" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
